import React, { useEffect } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Field,
  Flex,
  Heading,
  Input,
  Message,
  SwitchRoot,
  SwitchThumb,
  Text,
  toast,
} from '@workwhile/ui'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ModalSelect, ModalWithSelect } from 'components/ModalSelect'
import { CompanyPicker } from './CompanyPicker'
import { CompanyFilePicker } from '../../../components/CompanyFilePicker'
import { DeliveryHourInput } from '../../../components/DeliveryHourInput'
import {
  createIndeedFlexCompanyFlowSchema,
  CreateIndeedFlexCompanyFlowSchema,
} from '../schemas/createCompany'
import { useCreateIndeedFlexCompanyFlowContextValue } from '../context/CreateIndeedFlexCompanyFlowProvider'
import { useIndeedFlexCompaniesWorkflowsContextValue } from '../../../context/IndeedFlexCompaniesWorkflowsProvider'
import { useIndeedFlexCompaniesContextValue } from '../../../context/IndeedFlexCompaniesProvider'

export const CreateIndeedFlexCompanyFlowMain = () => {
  const {
    createCompanyFlow: { isOpen, close },
  } = useIndeedFlexCompaniesWorkflowsContextValue()
  const {
    state: { clients },
  } = useIndeedFlexCompaniesContextValue()
  const {
    state: {
      mutationState: { isPending, errorMessage },
    },
    actions: { submit, reset: resetMutation },
  } = useCreateIndeedFlexCompanyFlowContextValue()

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
    register,
    setValue,
  } = useForm<CreateIndeedFlexCompanyFlowSchema>({
    resolver: zodResolver(createIndeedFlexCompanyFlowSchema),
    defaultValues: {
      ifClientId: undefined,
      companyId: undefined,
      canCreateShifts: true,
      requiresSignedForm: false,
      fileHandleId: undefined,
      shiftNotificationTitle: '',
      shiftNotificationMessage: '',
      shiftNotificationDeliveryHour: null,
    },
  })

  const companyId = useWatch({ control, name: 'companyId' })
  const ifClientId = useWatch({ control, name: 'ifClientId' })
  const requiresSignedForm = useWatch({
    control,
    name: 'requiresSignedForm',
  })

  const clientRequiresCertificationType =
    clients.find((client) => client.ifClientId === ifClientId)
      ?.requiresCertificationType ?? false

  useEffect(() => {
    if (!isOpen) return
    reset({
      ifClientId: undefined,
      companyId: undefined,
      canCreateShifts: true,
      requiresSignedForm: false,
      fileHandleId: undefined,
      shiftNotificationTitle: '',
      shiftNotificationMessage: '',
      shiftNotificationDeliveryHour: null,
    })
  }, [isOpen, reset])

  useEffect(() => {
    if (clientRequiresCertificationType) {
      setValue('requiresSignedForm', true)
    }
  }, [clientRequiresCertificationType, setValue])

  const clientOptions = clients.map((client) => ({
    label: `${client.name} (${client.ifClientId})`,
    value: client.ifClientId,
  }))

  const handleClose = () => {
    reset()
    resetMutation()
    close()
  }

  const onSubmit = handleSubmit((data) => {
    submit(
      {
        ifClientId: data.ifClientId,
        companyId: data.companyId,
        canCreateShifts: data.canCreateShifts,
        ...(data.requiresSignedForm && data.fileHandleId != null
          ? { fileHandleId: data.fileHandleId }
          : {}),
        shiftNotificationTitle: data.shiftNotificationTitle ?? null,
        shiftNotificationMessage: data.shiftNotificationMessage ?? null,
        shiftNotificationDeliveryHour:
          data.shiftNotificationDeliveryHour ?? null,
      },
      {
        onSuccess: () => {
          toast.success('Indeed Flex company created', {
            position: 'top-center',
          })
          handleClose()
        },
      }
    )
  })

  return (
    <ModalWithSelect
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      showCancel
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Create
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
          Add Indeed Flex company
        </Heading>

        <Box mt={4}>
          <Controller
            control={control}
            name="ifClientId"
            render={({ field }) => (
              <Field
                label="Indeed Flex client"
                error={errors.ifClientId?.message}
              >
                <ModalSelect
                  options={clientOptions}
                  value={
                    clientOptions.find(
                      (option) => option.value === field.value
                    ) || null
                  }
                  onChange={(option) =>
                    field.onChange(option?.value ?? undefined)
                  }
                  placeholder="Select a client"
                  isClearable
                />
              </Field>
            )}
          />
        </Box>

        <Box mt={3}>
          <Controller
            control={control}
            name="companyId"
            render={({ field }) => (
              <CompanyPicker
                value={field.value}
                onChange={(nextCompanyId) => {
                  field.onChange(nextCompanyId)
                  setValue('fileHandleId', undefined)
                }}
                error={errors.companyId?.message}
              />
            )}
          />
        </Box>

        <Box mt={3}>
          <Field label="Create shifts automatically">
            <Controller
              control={control}
              name="canCreateShifts"
              render={({ field }) => (
                <SwitchRoot
                  checked={field.value}
                  onCheckedChange={(value) => field.onChange(value)}
                  aria-label="Create shifts automatically"
                >
                  <SwitchThumb>{field.value ? 'Yes' : 'No'}</SwitchThumb>
                </SwitchRoot>
              )}
            />
          </Field>
        </Box>

        <Box mt={3}>
          <Controller
            control={control}
            name="requiresSignedForm"
            render={({ field }) => (
              <Checkbox
                value={field.value}
                disabled={clientRequiresCertificationType}
                onChange={(checked) => {
                  if (clientRequiresCertificationType) return
                  field.onChange(checked)
                  if (!checked) {
                    setValue('fileHandleId', undefined)
                  }
                }}
              >
                Company requires signed form
              </Checkbox>
            )}
          />
          {clientRequiresCertificationType ? (
            <Text fontSize={1} color="lightText" mt={1}>
              Required by the selected Indeed Flex client.
            </Text>
          ) : null}
        </Box>

        {requiresSignedForm ? (
          <Box mt={3}>
            <Controller
              control={control}
              name="fileHandleId"
              render={({ field }) => (
                <CompanyFilePicker
                  companyId={companyId}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.fileHandleId?.message}
                  enabled={requiresSignedForm}
                />
              )}
            />
          </Box>
        ) : null}

        <Box mt={4}>
          <Text fontSize={2} fontWeight="600">
            Custom shift notification (optional)
          </Text>
          <Text fontSize={1} color="lightText" mt={1}>
            Applied automatically for shifts created from Indeed Flex. Delivery
            hour is relative to shift start (e.g. -2 = 2 hours before).
          </Text>
        </Box>

        <Box mt={3}>
          <Field
            label="Notification title"
            error={errors.shiftNotificationTitle?.message}
          >
            <Input
              {...register('shiftNotificationTitle')}
              placeholder="URGENT: GET PAID EARLY!!"
              maxLength={100}
            />
          </Field>
        </Box>

        <Box mt={3}>
          <Field
            label="Notification message"
            error={errors.shiftNotificationMessage?.message}
          >
            <Input
              {...register('shiftNotificationMessage')}
              placeholder="Clock in and out onsite..."
              maxLength={200}
            />
          </Field>
        </Box>

        <Box mt={3}>
          <Field
            label="Delivery hour"
            error={errors.shiftNotificationDeliveryHour?.message}
          >
            <Controller
              control={control}
              name="shiftNotificationDeliveryHour"
              render={({ field }) => (
                <DeliveryHourInput
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </Field>
        </Box>

        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </ModalWithSelect>
  )
}
